import { useState } from "react";
import {
  ArrowLeft,
  Bot,
  Sparkles,
  Zap,
  Brain,
  Users,
  ChevronRight,
} from "lucide-react";
import { apiv2, type ExecutionMode } from "../apiv2";
import GreenTendrils from "./GreenTendrils";

interface Props {
  projectId: string;
  projectName: string;
  onSelectAutonomous: () => void;
  onSelectDirect: () => void;
  onBack: () => void;
}

export default function ProjectModeSelector({
  projectId,
  projectName,
  onSelectAutonomous,
  onSelectDirect,
  onBack,
}: Props) {
  const [saving, setSaving] = useState(false);

  const handleSelect = async (mode: ExecutionMode) => {
    setSaving(true);
    try {
      await apiv2.updateProject(projectId, { execution_mode: mode });
      if (mode === "autonomous") {
        onSelectAutonomous();
      } else {
        onSelectDirect();
      }
    } catch {
      // fallback — navigate anyway
      if (mode === "autonomous") onSelectAutonomous();
      else onSelectDirect();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="overflow-y-auto custom-scrollbar h-full bg-surface-500 relative">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-16 space-y-12">
        {/* Header */}
        <div className="animate-fade-in">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-white/30 hover:text-white/60 transition mb-6"
          >
            <ArrowLeft size={16} /> Back to projects
          </button>

          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-4"
            style={{ background: "rgba(134,188,37,0.08)" }}
          >
            <Sparkles size={12} />
            {projectName}
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">
            <span className="gradient-text-green">How</span>
            <span className="text-white/40 font-extralight">
              {" "}
              would you like to work?
            </span>
          </h1>
          <p className="text-sm text-white/40 max-w-2xl">
            Choose how you want to interact with Zero Touch Data agents for this
            project. You can change this later in project settings.
          </p>
        </div>

        {/* Mode cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Autonomous Mode */}
          <button
            onClick={() => handleSelect("autonomous")}
            disabled={saving}
            className="group flex flex-col text-left p-8 rounded-2xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            style={{
              background: "rgba(28,28,42,0.6)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(134,188,37,0.4)";
              e.currentTarget.style.boxShadow = "0 0 30px rgba(134,188,37,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(134,188,37,0.2), rgba(134,188,37,0.05))",
                }}
              >
                <Brain size={24} className="text-brand-400" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              Autonomous Mode (Auto-pilot)
            </h2>
            <p className="text-sm text-white/40 mb-6 leading-relaxed">
              Tell me what you want to achieve. I'll create a plan, ask the
              right questions, and run the appropriate agents in sequence —
              guiding you through the entire process.
            </p>

            <div className="space-y-2 mb-6">
              {[
                "Describe your goal in natural language",
                "AI generates a step-by-step plan",
                "Agents run automatically with guided Q&A",
                "Review and approve results at each step",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-white/30"
                >
                  <Zap
                    size={12}
                    className="text-brand-400 mt-0.5 flex-shrink-0"
                  />
                  {item}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-sm font-medium text-brand-400 group-hover:gap-3 transition-all">
              Start with Autonomous Mode <ChevronRight size={16} />
            </div>
          </button>

          {/* Direct Mode */}
          <button
            onClick={() => handleSelect("direct")}
            disabled={saving}
            className="group flex flex-col text-left p-8 rounded-2xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
            style={{
              background: "rgba(28,28,42,0.6)",
              border: "1px solid rgba(168,85,247,0.15)",
              boxShadow: "0 0 0 0 rgba(168,85,247,0)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(168,85,247,0.4)";
              e.currentTarget.style.boxShadow = "0 0 30px rgba(168,85,247,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(168,85,247,0.15)";
              e.currentTarget.style.boxShadow = "0 0 0 0 rgba(168,85,247,0)";
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(168,85,247,0.2), rgba(168,85,247,0.05))",
                }}
              >
                <Bot size={24} className="text-purple-400" />
              </div>
              <div className="px-2 py-0.5 rounded text-[11px] font-bold text-purple-400 bg-purple-500/10 border border-purple-500/20">
                RECOMMENDED
              </div>
            </div>

            <h2 className="text-xl font-bold text-white mb-2">
              Direct Agent Selection (Co-pilot)
            </h2>
            <p className="text-sm text-white/40 mb-6 leading-relaxed">
              Choose specific agents from the catalog and work with them
              directly. Full control over which agent to use and what inputs to
              provide.
            </p>

            <div className="space-y-2 mb-6">
              {[
                "Browse the full agent catalog",
                "Select and configure agents individually",
                "Provide inputs directly to each agent",
                "Run agents on your own schedule",
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 text-xs text-white/30"
                >
                  <Users
                    size={12}
                    className="text-purple-400 mt-0.5 flex-shrink-0"
                  />
                  {item}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-sm font-medium text-purple-400 group-hover:gap-3 transition-all">
              Browse Agent Catalog <ChevronRight size={16} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
